import { useEffect, useRef } from 'react';
import { Dispatch } from 'react';
import { AppRouteAction, SearchItem } from '../types';

interface UseDataReadyStateProps {
  searchItems: SearchItem[];
  mappingContent: any;
  isLoading: boolean;
  view: string;
  source: string;
  tab: string;
  dispatch: Dispatch<AppRouteAction>;
}

export const useDataReadyState = ({
  searchItems,
  mappingContent,
  isLoading,
  view,
  source,
  tab,
  dispatch,
}: UseDataReadyStateProps) => {
  const prevDataRef = useRef<{
    searchItems: SearchItem[];
    mappingContent: any;
    view: string;
    source: string;
    tab: string;
  } | null>(null);

  const dataReadyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (dataReadyTimeoutRef.current) {
      clearTimeout(dataReadyTimeoutRef.current);
    }

    if (isLoading) {
      dispatch({ type: 'SET_IS_DATA_READY', payload: false });
      return;
    }

    const hasRequiredData = Boolean(mappingContent && view && source && tab);

    if (!hasRequiredData) {
      dispatch({ type: 'SET_IS_DATA_READY', payload: false });
      return;
    }

    const currentData = {
      searchItems,
      mappingContent,
      view,
      source,
      tab,
    };

    const dataChanged =
      !prevDataRef.current || JSON.stringify(prevDataRef.current) !== JSON.stringify(currentData);

    if (dataChanged) {
      dispatch({ type: 'SET_IS_DATA_READY', payload: false });

      dataReadyTimeoutRef.current = setTimeout(() => {
        dispatch({ type: 'SET_IS_DATA_READY', payload: true });
        prevDataRef.current = currentData;
      }, 100);
    } else if (hasRequiredData) {
      dispatch({ type: 'SET_IS_DATA_READY', payload: true });
    }

    return () => {
      if (dataReadyTimeoutRef.current) {
        clearTimeout(dataReadyTimeoutRef.current);
      }
    };
  }, [searchItems, mappingContent, isLoading, view, source, tab, dispatch]);

  useEffect(() => {
    return () => {
      if (dataReadyTimeoutRef.current) {
        clearTimeout(dataReadyTimeoutRef.current);
      }
    };
  }, []);
};
